import { useEffect, useRef, useState } from "react";

interface CountdownCircleProps {
  seconds: number;
  radius?: number;
  textClassName?: string;
  strokeWidth?: number;
  strokeBackground?: string;
  strokeForeground?: string;
  onComplete?: () => void;
  isPaused?: boolean;
}

export const CountdownCircle = ({
  seconds,
  radius = 40,
  textClassName,
  strokeWidth = 8,
  strokeBackground = "#e5e7eb",
  strokeForeground = "#6366f1",
  onComplete,
  isPaused,
}: CountdownCircleProps) => {
  const [timeLeft, setTimeLeft] = useState(seconds);
  const [progress, setProgress] = useState(0);
  const elapsedRef = useRef(0);
  const circumference = 2 * Math.PI * radius;

  useEffect(() => {
    let animationFrame: number;
    let lastTimestamp: number | undefined;
    let canceled = false;
    if (isPaused) {
      return;
    }

    const animate = (timestamp: number) => {
      if (canceled) {
        return;
      }

      if (lastTimestamp === undefined) {
        lastTimestamp = timestamp;
      }
      const elapsed = timestamp - lastTimestamp;
      lastTimestamp = timestamp;
      elapsedRef.current += elapsed;
      const currentTimeLeft = Math.max(
        seconds - Math.floor(elapsedRef.current / 1000),
        0
      );
      setTimeLeft(currentTimeLeft);
      setProgress(Math.min(elapsedRef.current / 1000 / seconds, 1));
      if (elapsedRef.current < seconds * 1000) {
        animationFrame = requestAnimationFrame(animate);
      } else if (currentTimeLeft <= 0 && onComplete) {
        onComplete();
      }
    };
    animationFrame = requestAnimationFrame(animate);
    return () => {
      canceled = true;
      cancelAnimationFrame(animationFrame);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seconds, isPaused]);

  useEffect(() => {
    elapsedRef.current = 0;
  }, [seconds]);

  return (
    <div
      className="relative flex items-center justify-center"
      style={{ width: radius * 2 + 20, height: radius * 2 + 20 }}
    >
      <svg
        width={radius * 2 + 20}
        height={radius * 2 + 20}
        className="absolute top-0 left-0 size-full"
      >
        <circle
          cx={radius + 10}
          cy={radius + 10}
          r={radius}
          fill="none"
          stroke={strokeBackground}
          strokeWidth={strokeWidth}
        />
        <circle
          cx={radius + 10}
          cy={radius + 10}
          r={radius}
          fill="none"
          stroke={strokeForeground}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={circumference - progress * circumference}
          strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 0.1s linear" }}
        />
      </svg>
      <span
        className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 ${textClassName}`}
      >
        {timeLeft}s
      </span>
    </div>
  );
};
